/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, August 22nd 2022, 9:07:33 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import {
  FormField,
  ICondition,
  isValueBasedCondition,
  CONDITION_OPERATORS_HELP_TEXT,
  CONDITION_OPERATORS,
} from "lib/schemas";
import { FormPicker, FormPickerProps, FieldPicker, FieldPickerProps } from "form-builder/components";
import { ValueSelector, InputHelpDialogWrapper } from "ui-widgets";
import { ConditionOperator } from "./ConditionOperator";

export interface ConditionSettingsProps {
  conditionSettings: ICondition;
  onChangeConditionForm: FormPickerProps["handleChooseForm"];
  onChangeConditionField: FieldPickerProps["handleChooseField"];
  onChangeConditionOperator: (condition_operator: ICondition["condition_operator"]) => void;
  onChangeConditionValue: (condition_value: ICondition["condition_value"]) => void;
  showFormPicker?: boolean;
}

export const ConditionSettings = (props: ConditionSettingsProps) => {
  const {
    conditionOperatorHelpText,
    conditionValueChoices,
    showConditionValue,
    showConditionOperator,
    handleChooseForm,
    handleChooseField,
    onLoadInitialField,
  } = useConditionSettingsLogic(props);

  const {
    conditionSettings: { condition_form, condition_field, condition_operator, condition_value } = {},
    showFormPicker = true,
    onChangeConditionOperator,
    onChangeConditionValue,
  } = props;

  const conditionOperatorComponent = (
    <ConditionOperator condition_operator={condition_operator} onChangeConditionOperator={onChangeConditionOperator} />
  );

  return (
    <Grid container spacing={2}>
      {showFormPicker && (
        <Grid item xs={12}>
          <FormPicker handleChooseForm={handleChooseForm} formId={condition_form} label="Choose Form" />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <FieldPicker
          onLoadIntial={onLoadInitialField}
          handleChooseField={handleChooseField}
          fieldIdentifier={condition_field}
          label="Choose Field"
          formId={condition_form as number}
          apiQueryParams={{ include_choices: true }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        {showConditionOperator && (
          <Stack>
            {conditionOperatorHelpText ? (
              <InputHelpDialogWrapper
                helpTitle={CONDITION_OPERATORS[condition_operator]}
                helpContent={conditionOperatorHelpText}
              >
                {conditionOperatorComponent}
              </InputHelpDialogWrapper>
            ) : (
              conditionOperatorComponent
            )}
          </Stack>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        {showConditionValue ? (
          <Stack>
            <ValueSelector
              label="Choose Condition Value"
              chosenValue={condition_value}
              onChangeValue={onChangeConditionValue}
              choices={conditionValueChoices}
            />
          </Stack>
        ) : null}
      </Grid>
    </Grid>
  );
};

const useConditionSettingsLogic = ({
  conditionSettings,
  onChangeConditionForm,
  onChangeConditionField,
}: ConditionSettingsProps) => {
  const [selectedField, setSelectedField] = React.useState<FormField>();
  const conditionValueChoices = selectedField?.resolved_choices;
  const conditionOperatorHelpText = CONDITION_OPERATORS_HELP_TEXT[conditionSettings.condition_operator];

  return {
    conditionOperatorHelpText,
    conditionValueChoices,
    showConditionOperator: Boolean(selectedField),
    showConditionValue: Boolean(selectedField) && isValueBasedCondition(conditionSettings.condition_operator),
    handleChooseForm: onChangeConditionForm,
    handleChooseField: (field: FormField) => {
      setSelectedField(field);
      onChangeConditionField(field);
    },
    onLoadInitialField: (field: FormField) => setSelectedField(field),
  };
};
